import React, { useRef, useEffect, useState } from "react";
import styled from "styled-components";
import { Markup } from "interweave";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { motion, useInView } from "framer-motion";
import { wrap } from "@popmotion/popcorn";
import { useWindowSize } from "../../hooks";
import { BrightPrenatalSeq, Exseq } from "../../assets/svgs";
import { Button } from "../../components";

const ServiceOverview = ({ slides }) => {
  const { windowWidth } = useWindowSize();

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => Math.abs(offset) * velocity;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const indicatorWidth = 210 / slides.length;

  const [[page, direction], setPage] = useState([0, 0]);
  const [pause, setPause] = useState(true);
  const index = wrap(0, slides.length, page);
  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  let timer;

  useEffect(() => {
    clearTimeout(timer);
    if (!pause) {
      timer = setTimeout(() => {
        paginate(1);
      }, 8000);
    }
  }, [page, pause]);

  useEffect(() => {
    if (isInView) {
      setPause(false);
      setPage([0, 1]);
    }
  }, [isInView]);

  console.log(slides);

  return (
    <Container ref={ref}>
      <Card id="service-overview" color={slides[index].color || "#1AE299"}>
        <VisuallyHidden>Featured services and events</VisuallyHidden>
        <Track>
          <Slide
            type={slides[index].type}
            key={page}
            custom={direction}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              x: { type: "spring", stiffness: 300, damping: 300 },
              opacity: { duration: 1 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);
              clearTimeout(timer);
              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
          >
            {slides[index].type === "service" && (
              <>
                <ServiceLogo>
                  {slides[index].title === "PrenatalSEQ" ? (
                    <BrightPrenatalSeq />
                  ) : slides[index].title === "Gene Panels" ? (
                    <Exseq />
                  ) : (
                    <h3>{slides[index].title}</h3>
                  )}
                </ServiceLogo>
                <ServiceDescription>
                  <p>
                    <Markup content={slides[index].description} />
                  </p>
                </ServiceDescription>
              </>
            )}
            {slides[index].type === "event" && (
              <>
                <Content>
                  <Tag>Event</Tag>
                  <h3>{slides[index].title}</h3>
                  <h4>
                    <Markup content={slides[index].date} />
                  </h4>
                  <p>
                    <Markup content={slides[index].description} />
                  </p>
                </Content>
                <LinkToEvent href={slides[index].linkToEvent}>
                  <GatsbyImage
                    image={getImage(slides[index].previewImage)}
                    alt={slides[index].title}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: "2rem",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Button
                      type="button"
                      solid="var(--genseq-purple)"
                      text="Watch Here"
                      maxWidth="180px"
                      href={slides[index].linkToEvent}
                    />
                  </div>
                </LinkToEvent>
              </>
            )}
          </Slide>
        </Track>
        <Indicator
          width={indicatorWidth * (index + 1)}
          color={slides[index].color || "#1AE299"}
        />
      </Card>
    </Container>
  );
};

const VisuallyHidden = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Container = styled(motion.section)`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  position: relative;
  padding: 6rem 0 12rem 0;
  @media screen and (min-width: 1024px) {
    padding: 12rem 0;
    grid-template-columns: repeat(12, 1fr);
  }
`;

const Card = styled(motion.div)`
  background-color: #ffffff;
  border-radius: 10px;
  cursor: pointer;
  align-items: center;
  display: flex;
  flex-direction: column;
  width: calc(100% - 2.5rem);
  justify-content: space-between;
  max-width: 1400px;
  transform: translate(-50%, 0);
  position: absolute;
  top: -120px;
  left: 50%;
  z-index: 5;
  min-height: 640px;
  overflow: hidden;
  h3 {
    color: ${({ color }) => color};
    font-size: ${({ type }) => (type === "event" ? "2rem" : "1.5rem")};
    font-weight: 400;
  }
  p {
    font-size: 1rem;
    line-height: 1.3;
    font-weight: 300;
  }

  hr {
    margin: 2rem 0;
  }
  @media screen and (min-width: 1024px) {
    top: -90px;
    width: calc(100% - 8rem);
    min-height: 420px;
    h3 {
      font-size: ${({ type }) => (type === "event" ? "3rem" : "2.5rem")};
    }
    p {
      font-size: 1.25rem;
    }
  }
  @media screen and (max-width: 1200px) and (min-height: 1200px) {
    top: -240px;
    min-height: 620px;
  }
`;

const Track = styled(motion.div)`
  cursor: grab;
  display: flex;
  flex-wrap: nowrap;
  height: 100%;
  width: 100%;
  position: relative;
  &:active {
    cursor: grabbing;
  }
`;

const Slide = styled(motion.div)`
  display: ${({ type }) => (type === "event" ? "grid" : "flex")};
  flex-direction: column;
  grid-template-rows: ${({ type }) =>
    type === "event" ? "1fr 160px" : "unset"};
  gap: 1.5rem;
  height: 100%;
  width: 100%;
  svg {
    width: auto;
    height: 100%;
    max-width: 198px;
    max-height: 24px;
    margin-right: auto;
  }
  @media screen and (min-width: 1024px) {
    display: grid;
    gap: 3rem;
    grid-template-columns: ${({ type }) =>
      type === "event" ? "1fr 295px" : "275px 1fr"};
    grid-template-rows: 1fr;
    svg {
      max-width: 100%;
      max-height: 36px;
    }
  }
`;

const Indicator = styled.div`
  width: 210px;
  height: 1px;
  background: #e5e5e5;
  position: absolute;
  overflow: visible;
  bottom: 1rem;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 10;
  &:before {
    content: "";
    background: ${({ color }) => color};
    width: ${({ width }) => `${width}px`};
    left: 0;
    height: 0.25rem;
    position: absolute;
    top: -1.5px;
    transition: all 0.5s ease-in-out;
  }
  @media screen and (min-width: 1024px) { 
    bottom: 2rem;
  }
`;

const ServiceLogo = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 2rem 1.25rem 0 1.25rem;
  @media screen and (min-width: 1024px) {
    padding: 5rem 0 4rem 3.625rem;
  }
`;

const ServiceDescription = styled.div`
  padding: 0 1.25rem 2rem 1.25rem;
  @media screen and (min-width: 1024px) {
    padding: 5rem 3.625rem 4rem 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin: auto 0;
  padding: 2rem 1.25rem;
  @media screen and (min-width: 1024px) {
    padding: 2rem 3.625rem 4rem 3.625rem;
  }
`;

const LinkToEvent = styled.a`
  position: relative;
  .gatsby-image-wrapper {
    height: 100%;
  }
`;

const Tag = styled.div`
  background-color: var(--genseq-blue);
  border-radius: 50px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  text-align: center;
  text-transform: uppercase;
  width: 128px;
`;

export default ServiceOverview;
